/* ============================================================================
   useDiveSites — THE WIRING SEAM
   ----------------------------------------------------------------------------
   This is the ONLY file to edit when wiring Dive Sites into the live system.
   DiveSitesPage.tsx and DiveSitesPage.css are the locked visual and must not
   change during wiring. Everything the page needs — the data and the six
   mutations — comes from this hook. Today it holds mock data in memory; to go
   live, replace each marked SEAM with the real operator-scoped API call.

   Field mapping (view-model -> database), for the engineer:
     id                -> divesites.dive_site_id
     name              -> divesites.name                 (canonical / "also known as")
     type              -> divesites.dive_type
     entry             -> divesites.entry_type
     skill             -> divesites.skill_level
     depthMin/depthMax -> divesites.depth_min_meters / depth_max_meters
     description        -> divesites.description          (canonical)
     inList            -> a row exists in operator_dive_sites for this operator
     active            -> operator_dive_sites.is_active
     customName        -> operator_dive_sites.custom_name        (add in migration)
     customDescription -> operator_dive_sites.custom_description
     isCustom          -> divesites.owner_operator_id = this operator (custom site)

   Tenant rule (non-negotiable): every read and write below is scoped to the
   authenticated operator server-side (req.operator_id from requireDashboardScope)
   and, for custom sites, to owner_operator_id. Row-Level Security enforces it at
   the database. No operator_id is ever accepted from the client.
   ============================================================================ */
import { useState, useCallback, useEffect } from "react";
import { apiFetch } from "../../../utils/api";

export type DiveSite = {
  id: number;
  name: string;
  type: string;
  entry: string;
  skill: string;
  depthMin: number;
  depthMax: number;
  description: string;
  inList: boolean;
  active: boolean;
  customName: string;
  customDescription: string;
  isCustom: boolean;
  heroImageUrl?: string | null;
  heroCaption?: string | null;
};

export type NewCustomSite = {
  name: string;
  type: string;
  entry: string;
  skill: string;
  depthMin: number;
  depthMax: number;
  description: string;
};

export type MeetLocation = {
  id: number;
  name: string;
  active: boolean;
  sortOrder: number;
};

export type NewMeetLocation = {
  name: string;
  active: boolean;
};

export type DiveCategory = {
  id: number;
  value: string;
  label: string;
  active: boolean;
  sortOrder: number;
};

export type NewDiveCategory = {
  value: string;
  label: string;
  active: boolean;
};

export type DiveSitesApi = {
  sites: DiveSite[];
  meetLocations: MeetLocation[];
  diveCategories: DiveCategory[];
  loading: boolean;
  /** The operator's destination name, for on-page labels. Never hardcoded.
   *  SEAM: from destinations.name via the operator's destination_id. */
  destinationName: string;
  /** Add a catalog site to this operator's list (is_active = true). */
  addToList: (id: number) => void;
  /** Remove a catalog site from this operator's list (archive/detach, never destroy the catalog row). */
  removeFromList: (id: number) => void;
  /** Flip is_active for a curated site. */
  setActive: (id: number, active: boolean) => void;
  /** Save the operator overlay: custom_name and custom_description (and active) for a catalog site,
   *  or name/description (and active) for a custom site the operator owns. */
  saveOverlay: (id: number, patch: { customName?: string; customDescription?: string; name?: string; description?: string; active?: boolean }) => void;
  /** Upload the operator-specific hero image for a dive site and refresh canonical site data. */
  uploadHeroImage: (id: number, file: File, caption?: string) => Promise<void>;
  /** Create an operator-owned custom site and add it to the list. */
  createCustom: (site: NewCustomSite) => void;
  /** Delete an operator-owned custom site outright (only permitted on isCustom sites). */
  deleteCustom: (id: number) => void;
  /** Meet Locations v0.1: local UI seam until the operator-scoped API exists. */
  createMeetLocation: (location: NewMeetLocation) => void;
  updateMeetLocation: (id: number, patch: Partial<NewMeetLocation>) => void;
  setMeetLocationActive: (id: number, active: boolean) => void;
  createDiveCategory: (category: NewDiveCategory) => void;
  updateDiveCategory: (id: number, patch: Partial<NewDiveCategory>) => void;
  setDiveCategoryActive: (id: number, active: boolean) => void;
};

/**
 * Data + mutations for the Dive Sites page.
 *
 * TO WIRE THE LIVE SYSTEM, replace each SEAM body:
 *   - the initial state -> GET the operator's catalog + curated overlay
 *     (SELECT from divesites, LEFT JOIN operator_dive_sites WHERE operator_id = <scope>),
 *     mirroring the existing pattern-lookups query that already filters by
 *     operator_id AND is_active.
 *   - each mutation -> the matching operator-scoped endpoint, then refresh.
 * Keep the return shape identical so DiveSitesPage.tsx never changes.
 */
export function useDiveSites(): DiveSitesApi {
  const [sites, setSites] = useState<DiveSite[]>([]);
  const [meetLocations, setMeetLocations] = useState<MeetLocation[]>([]);
  const [diveCategories, setDiveCategories] = useState<DiveCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [destinationName, setDestinationName] = useState("Local");

  const loadSites = useCallback(async () => {
    setLoading(true);

    try {
      const [sitesResponse, meetLocationsResponse, diveCategoriesResponse] = await Promise.all([
        apiFetch(
          "/api/v1/dashboard/dive-sites",
          { method: "GET" }
        ),
        apiFetch(
          "/api/v1/dashboard/dive-sites/meet-locations",
          { method: "GET" }
        ),
        apiFetch(
          "/api/v1/dashboard/dive-sites/dive-categories",
          { method: "GET" }
        ),
      ]);

      const sitesPayload = (await sitesResponse.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
        sites?: DiveSite[];
        destinationName?: string;
      };

      const meetLocationsPayload = (await meetLocationsResponse.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
        meetLocations?: MeetLocation[];
      };

      const diveCategoriesPayload = (await diveCategoriesResponse.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
        diveCategories?: DiveCategory[];
      };

      if (!sitesResponse.ok || sitesPayload.ok === false) {
        throw new Error(
          sitesPayload.message || `HTTP ${sitesResponse.status}`
        );
      }

      if (!meetLocationsResponse.ok || meetLocationsPayload.ok === false) {
        throw new Error(
          meetLocationsPayload.message || `HTTP ${meetLocationsResponse.status}`
        );
      }

      if (!diveCategoriesResponse.ok || diveCategoriesPayload.ok === false) {
        throw new Error(
          diveCategoriesPayload.message || `HTTP ${diveCategoriesResponse.status}`
        );
      }

      setSites(Array.isArray(sitesPayload.sites) ? sitesPayload.sites : []);
      setMeetLocations(
        Array.isArray(meetLocationsPayload.meetLocations)
          ? meetLocationsPayload.meetLocations
          : []
      );
      setDiveCategories(
        Array.isArray(diveCategoriesPayload.diveCategories)
          ? diveCategoriesPayload.diveCategories
          : []
      );
      setDestinationName(
        typeof sitesPayload.destinationName === "string" &&
          sitesPayload.destinationName.trim()
          ? sitesPayload.destinationName.trim()
          : "Local"
      );
    } catch (error) {
      console.error("[Dive Sites] Load failed:", error);
      setSites([]);
      setMeetLocations([]);
      setDiveCategories([]);
      setDestinationName("Local");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSites();
  }, [loadSites]);

  const addToList = useCallback(async (id: number) => {
    try {
      const response = await apiFetch(
        `/api/v1/dashboard/dive-sites/${id}/curation`,
        { method: "POST" }
      );

      const payload = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
      };

      if (!response.ok || payload.ok === false) {
        throw new Error(
          payload.message || `HTTP ${response.status}`
        );
      }

      await loadSites();
    } catch (error) {
      console.error("[Dive Sites] Add failed:", error);
    }
  }, [loadSites]);

  const removeFromList = useCallback(async (id: number) => {
    try {
      const response = await apiFetch(
        `/api/v1/dashboard/dive-sites/${id}/curation`,
        { method: "DELETE" }
      );

      const payload = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
      };

      if (!response.ok || payload.ok === false) {
        throw new Error(
          payload.message || `HTTP ${response.status}`
        );
      }

      await loadSites();
    } catch (error) {
      console.error("[Dive Sites] Remove failed:", error);
      window.alert(error instanceof Error ? error.message : "Dive site could not be deleted.");
    }
  }, [loadSites]);

  const setActive = useCallback(async (
    id: number,
    active: boolean
  ) => {
    try {
      const response = await apiFetch(
        `/api/v1/dashboard/dive-sites/${id}/curation`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_active: active }),
        }
      );

      const payload = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
      };

      if (!response.ok || payload.ok === false) {
        throw new Error(
          payload.message || `HTTP ${response.status}`
        );
      }

      await loadSites();
    } catch (error) {
      console.error("[Dive Sites] Status update failed:", error);
    }
  }, [loadSites]);

  const saveOverlay = useCallback(async (
    id: number,
    p: {
      customName?: string;
      customDescription?: string;
      name?: string;
      description?: string;
      active?: boolean;
    }
  ) => {
    const body: Record<string, unknown> = {};

    if (p.customName !== undefined) {
      body.custom_name = p.customName;
    }
    if (p.customDescription !== undefined) {
      body.custom_description = p.customDescription;
    }
    if (p.name !== undefined) {
      body.name = p.name;
    }
    if (p.description !== undefined) {
      body.description = p.description;
    }
    if (p.active !== undefined) {
      body.is_active = p.active;
    }

    try {
      const response = await apiFetch(
        `/api/v1/dashboard/dive-sites/${id}/overlay`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );

      const payload = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
      };

      if (!response.ok || payload.ok === false) {
        throw new Error(
          payload.message || `HTTP ${response.status}`
        );
      }

      await loadSites();
    } catch (error) {
      console.error("[Dive Sites] Save failed:", error);
    }
  }, [loadSites]);

  const createCustom = useCallback(async (
    site: NewCustomSite
  ) => {
    try {
      const response = await apiFetch(
        "/api/v1/dashboard/dive-sites/custom",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: site.name,
            dive_type: site.type,
            entry_type: site.entry,
            skill_level: site.skill,
            depth_min_meters: site.depthMin,
            depth_max_meters: site.depthMax,
            description: site.description,
          }),
        }
      );

      const payload = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
      };

      if (!response.ok || payload.ok === false) {
        throw new Error(
          payload.message || `HTTP ${response.status}`
        );
      }

      await loadSites();
    } catch (error) {
      console.error("[Dive Sites] Create failed:", error);
    }
  }, [loadSites]);

  const deleteCustom = useCallback(async (id: number) => {
    try {
      const response = await apiFetch(
        `/api/v1/dashboard/dive-sites/${id}/custom`,
        { method: "DELETE" }
      );

      const payload = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
      };

      if (!response.ok || payload.ok === false) {
        throw new Error(
          payload.message || `HTTP ${response.status}`
        );
      }

      await loadSites();
    } catch (error) {
      console.error("[Dive Sites] Delete failed:", error);
      window.alert(error instanceof Error ? error.message : "Dive site could not be deleted.");
    }
  }, [loadSites]);

  const uploadHeroImage = useCallback(async (
    id: number,
    file: File,
    caption?: string
  ) => {
    const formData = new FormData();
    formData.append("image", file);
    if (caption && caption.trim()) {
      formData.append("caption", caption.trim());
    }

    const response = await apiFetch(
      `/api/v1/dashboard/dive-sites/${id}/hero-image`,
      {
        method: "POST",
        body: formData,
      }
    );

    const payload = (await response.json().catch(() => ({}))) as {
      ok?: boolean;
      status?: string;
      message?: string;
      heroImageUrl?: string;
    };

    if (!response.ok || payload.ok === false || !payload.heroImageUrl) {
      throw new Error(
        payload.message || `HTTP ${response.status}`
      );
    }

    await loadSites();
  }, [loadSites]);

  const createMeetLocation = useCallback(async (
    location: NewMeetLocation
  ) => {
    try {
      const response = await apiFetch(
        "/api/v1/dashboard/dive-sites/meet-locations",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ label: location.name }),
        }
      );

      const payload = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
      };

      if (!response.ok || payload.ok === false) {
        throw new Error(
          payload.message || `HTTP ${response.status}`
        );
      }

      await loadSites();
    } catch (error) {
      console.error("[Dive Sites] Meet location create failed:", error);
    }
  }, [loadSites]);

  const updateMeetLocation = useCallback(async (
    id: number,
    patch: Partial<NewMeetLocation>
  ) => {
    const body: Record<string, unknown> = {};

    if (patch.name !== undefined) {
      body.label = patch.name;
    }
    if (patch.active !== undefined) {
      body.is_active = patch.active;
    }

    try {
      const response = await apiFetch(
        `/api/v1/dashboard/dive-sites/meet-locations/${id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );

      const payload = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
      };

      if (!response.ok || payload.ok === false) {
        throw new Error(
          payload.message || `HTTP ${response.status}`
        );
      }

      await loadSites();
    } catch (error) {
      console.error("[Dive Sites] Meet location update failed:", error);
    }
  }, [loadSites]);

  const setMeetLocationActive = useCallback(async (
    id: number,
    active: boolean
  ) => {
    await updateMeetLocation(id, { active });
  }, [updateMeetLocation]);

  const createDiveCategory = useCallback(async (
    category: NewDiveCategory
  ) => {
    try {
      const response = await apiFetch(
        "/api/v1/dashboard/dive-sites/dive-categories",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            category_value: category.value,
            label: category.label,
          }),
        }
      );

      const payload = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
      };

      if (!response.ok || payload.ok === false) {
        throw new Error(
          payload.message || `HTTP ${response.status}`
        );
      }

      await loadSites();
    } catch (error) {
      console.error("[Dive Sites] Dive category create failed:", error);
    }
  }, [loadSites]);

  const updateDiveCategory = useCallback(async (
    id: number,
    patch: Partial<NewDiveCategory>
  ) => {
    const body: Record<string, unknown> = {};

    if (patch.label !== undefined) {
      body.label = patch.label;
    }
    if (patch.active !== undefined) {
      body.is_active = patch.active;
    }

    try {
      const response = await apiFetch(
        `/api/v1/dashboard/dive-sites/dive-categories/${id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );

      const payload = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
      };

      if (!response.ok || payload.ok === false) {
        throw new Error(
          payload.message || `HTTP ${response.status}`
        );
      }

      await loadSites();
    } catch (error) {
      console.error("[Dive Sites] Dive category update failed:", error);
    }
  }, [loadSites]);

  const setDiveCategoryActive = useCallback(async (
    id: number,
    active: boolean
  ) => {
    await updateDiveCategory(id, { active });
  }, [updateDiveCategory]);

  return {
    sites,
    meetLocations,
    diveCategories,
    loading,
    destinationName,
    addToList,
    removeFromList,
    setActive,
    saveOverlay,
    uploadHeroImage,
    createCustom,
    deleteCustom,
    createMeetLocation,
    updateMeetLocation,
    setMeetLocationActive,
    createDiveCategory,
    updateDiveCategory,
    setDiveCategoryActive,
  };
}
